import React, { JSX } from "react";
import { useSelector } from "react-redux";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import { Autocomplete, Box, Table, TableHead, TextField, Typography } from "@mui/material";

import { RootState } from "../../../store";
import useAssessmentLabel from "../../../hooks/useAssessmentLabel";
import { DiagnosticTypes } from "../../../interfaces/LabInterfaces";
import { ServiceInterface } from "../../../interfaces/ServiceInterface";
import { XrayService, IntraOralValue } from "../../../interfaces/AssessmentInterfaces";
import { DentalServicesRow as XRayServicesRow } from "../DentalServices/DentalServicesRow";

// @ts-expect-error - css
import styles from "../Assessment.module.css";

interface PropType {
  intraOral: Record<"permanent" | "primary", Record<string, IntraOralValue>>;
  xrayServices: Array<XrayService>;
  handleAddService: (v: ServiceInterface) => void;
  handleUpdateService: (v: XrayService) => void;
  handleRemoveService: (i: Array<XrayService>, index: number) => void;
}

const filterActiveLabService = (
  services: Array<ServiceInterface>,
  existingServices: Array<XrayService>
) => {
  const existingIds = new Set(existingServices.map((s) => s.id));

  return services.filter(
    (service) =>
      service.active &&
      service?.labTestGroup &&
      service?.labTestGroup?.type === DiagnosticTypes.RADIOLOGY &&
      !existingIds.has(service.id)
  );
};

// eslint-disable-next-line import/prefer-default-export
export const XrayServices = ({
  xrayServices = [],
  intraOral,
  handleAddService,
  handleUpdateService,
  handleRemoveService
}: PropType): JSX.Element => {
  const assessmentLabels = useAssessmentLabel();
  const services = useSelector((state: RootState) => state.services.collection) || [];
  const filteredServices = filterActiveLabService(services, xrayServices);

  return (
    <Box className={styles.assessmentRow}>
      <Box className={styles.assessmentLabel} sx={{ display: "flex", alignItems: "center" }}>
        <Typography component="span" fontWeight="600">
          {assessmentLabels.xrayServices}
        </Typography>
      </Box>

      <Box className={styles.assessmentField}>
        <>
          <Box display="flex" alignItems="center" width="100%">
            <Autocomplete
              fullWidth
              options={filteredServices}
              value={[]}
              getOptionLabel={(option) => option.name || ""}
              renderOption={(params, option) => (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <li {...params} key={option.id}>
                  {option.name || ""}
                </li>
              )}
              onChange={(_, v) => {
                if (v) {
                  handleAddService(v);
                }
              }}
              renderInput={(params) => (
                <TextField
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...params}
                  label={assessmentLabels.xrayServices}
                  slotProps={{
                    inputLabel: { shrink: true }
                  }}
                  placeholder="Search and select services"
                  variant="outlined"
                  margin="dense"
                />
              )}
            />
          </Box>

          {xrayServices.length > 0 && (
            <Box sx={{ mt: "16px" }}>
              <TableContainer>
                <Table sx={{ minWidth: 650 }} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>SN.</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Teeth</TableCell>
                      <TableCell sx={{ width: "250px" }}>Note</TableCell>
                      <TableCell sx={{ textAlign: "right", width: "100px" }}>Price</TableCell>
                      <TableCell sx={{ textAlign: "right", width: "50px" }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {xrayServices?.map((item, i) => (
                      <XRayServicesRow
                        // eslint-disable-next-line react/no-array-index-key
                        key={`${item.name}-${i}`}
                        item={item}
                        index={i}
                        services={xrayServices}
                        handleUpdateService={handleUpdateService}
                        handleRemoveService={handleRemoveService}
                        intraOral={intraOral}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </>
      </Box>
    </Box>
  );
};
